import { useState, useEffect, useCallback, useMemo } from 'react';
import { Resizable } from 'react-resizable';
import 'react-resizable/css/styles.css';
import {
  Table,
  Card,
  Button,
  Space,
  Tag,
  Input,
  Select,
  DatePicker,
  Row,
  Col,
  Typography,
  message,
  Tooltip,
  Drawer,
  Form,
  Divider,
  Modal,
  Descriptions
} from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cogsService } from '../services/cogsService';
import { dimensionsService } from '../services/dimensionsService';
import CostingRuleDetails from '../components/CostingRuleDetails';
import COGSCreationModal from '../components/COGSCreationModal';
import { useAuth } from '../hooks/useAuth';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const COLUMN_WIDTHS_STORAGE_KEY = 'cogs_rules_column_widths';

// Load saved column widths from localStorage
const loadColumnWidths = () => {
  try {
    const saved = localStorage.getItem(COLUMN_WIDTHS_STORAGE_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch {
    return {};
  }
};

// Save column widths to localStorage
const saveColumnWidths = (widths) => {
  try {
    localStorage.setItem(COLUMN_WIDTHS_STORAGE_KEY, JSON.stringify(widths));
  } catch {
    // Silently ignore storage errors
  }
};

// Resizable header cell component
const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

// Helper to parse URL search params into filter object
const parseSearchParams = (searchParams) => {
  const filters = {};
  const pagination = { current: 1, pageSize: 20 };
  const sort = { field: null, order: null };

  for (const [key, value] of searchParams.entries()) {
    if (key === 'page') {
      pagination.current = parseInt(value, 10) || 1;
    } else if (key === 'pageSize') {
      pagination.pageSize = parseInt(value, 10) || 20;
    } else if (key === 'sortBy') {
      sort.field = value;
    } else if (key === 'sortOrder') {
      sort.order = value === 'ASC' ? 'ascend' : 'descend';
    } else if (['entityId', 'productId', 'channelId', 'schemeId', 'feeCurrencyCode', 'status'].includes(key)) {
      // Multi-value filters (stored as comma-separated)
      filters[key] = value.split(',').map(v => {
        const num = parseInt(v, 10);
        return isNaN(num) ? v : num;
      });
    } else if (key === 'startDate' || key === 'endDate') {
      filters[key] = value;
    } else if (value) {
      filters[key] = value;
    }
  }

  return { filters, pagination, sort };
};

// Helper to serialize state to URL search params
const serializeToSearchParams = (filters, pagination, sort) => {
  const params = new URLSearchParams();

  // Add pagination
  if (pagination.current > 1) {
    params.set('page', pagination.current.toString());
  }
  if (pagination.pageSize !== 20) {
    params.set('pageSize', pagination.pageSize.toString());
  }

  // Add sorting
  if (sort.field && sort.order) {
    params.set('sortBy', sort.field);
    params.set('sortOrder', sort.order === 'ascend' ? 'ASC' : 'DESC');
  }

  // Add filters
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value) && value.length > 0) {
        params.set(key, value.join(','));
      } else if (!Array.isArray(value)) {
        params.set(key, value.toString());
      }
    }
  });

  return params;
};

const AllCOGSRulesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse initial state from URL
  const initialState = parseSearchParams(searchParams);

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: initialState.pagination.current,
    pageSize: initialState.pagination.pageSize,
    total: 0
  });
  const [filters, setFilters] = useState(initialState.filters);
  const [dimensions, setDimensions] = useState({});
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [selectedRuleId, setSelectedRuleId] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [attachUrlRule, setAttachUrlRule] = useState(null);
  const [attachUrlVisible, setAttachUrlVisible] = useState(false);
  const [attachUrlLoading, setAttachUrlLoading] = useState(false);
  const [sortField, setSortField] = useState(initialState.sort.field);
  const [sortOrder, setSortOrder] = useState(initialState.sort.order);
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);
  const [form] = Form.useForm();

  const navigate = useNavigate();
  const { user } = useAuth();
  const userData = user?.data;

  // Sync state to URL whenever filters, pagination, or sort changes
  const updateURL = useCallback((newFilters, newPagination, newSort) => {
    const params = serializeToSearchParams(
      newFilters,
      newPagination,
      { field: newSort.field, order: newSort.order }
    );
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  // Load dimensions for filters
  const loadDimensions = useCallback(async () => {
    try {
      const dimensionData = await dimensionsService.getAllDimensions();
      setDimensions(dimensionData);
    } catch (error) {
      // Silently handle dimension loading error
    }
  }, []);

  // Load COGS rules with filters and pagination
  const loadRules = useCallback(async (page = 1, pageSize = 20, filterParams = {}, sort = {}) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: pageSize,
        ...filterParams,
        ...sort
      };

      const response = await cogsService.getRules(params);
      setRules(response.rules || []);
      setPagination({
        current: page,
        pageSize,
        total: response.pagination?.total || 0
      });
    } catch (error) {
      message.error('Failed to load COGS rules');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load - use state initialized from URL
  useEffect(() => {
    loadDimensions();

    // Build sort params from initial state
    const sortParams = initialState.sort.field && initialState.sort.order
      ? { sortBy: initialState.sort.field, sortOrder: initialState.sort.order === 'ascend' ? 'ASC' : 'DESC' }
      : {};

    loadRules(
      initialState.pagination.current,
      initialState.pagination.pageSize,
      initialState.filters,
      sortParams
    );

    // Populate the filter form with initial values from URL
    if (Object.keys(initialState.filters).length > 0) {
      form.setFieldsValue(initialState.filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Helper function to get current sort parameters
  const getSortParams = useCallback(() => {
    if (sortField && sortOrder) {
      return {
        sortBy: sortField,
        sortOrder: sortOrder === 'ascend' ? 'ASC' : 'DESC'
      };
    }
    return {};
  }, [sortField, sortOrder]);

  // Handle table pagination change
  const handleTableChange = (paginationInfo, _tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;

    // Process sorting
    let sortParams = {};
    let newSortField = null;
    let newSortOrder = null;

    if (sorter && sorter.field && sorter.order) {
      const sortFieldName = Array.isArray(sorter.field) ? sorter.field.join('.') : sorter.field;
      sortParams = {
        sortBy: sortFieldName,
        sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
      };
      newSortField = sortFieldName;
      newSortOrder = sorter.order;
    }

    setSortField(newSortField);
    setSortOrder(newSortOrder);

    // Update URL with new pagination and sort
    updateURL(filters, { current, pageSize }, { field: newSortField, order: newSortOrder });

    loadRules(current, pageSize, filters, sortParams);
  };

  // Handle filter application
  const handleFilterApply = () => {
    const filterValues = form.getFieldsValue();
    const processedFilters = {};

    // Process date ranges
    if (filterValues.dateRange) {
      processedFilters.startDate = filterValues.dateRange[0]?.toISOString();
      processedFilters.endDate = filterValues.dateRange[1]?.toISOString();
    }

    // Process other filters
    Object.keys(filterValues).forEach(key => {
      if (key !== 'dateRange' && filterValues[key] !== undefined && filterValues[key] !== '') {
        processedFilters[key] = filterValues[key];
      }
    });

    setFilters(processedFilters);

    // Reset to page 1 when filters change, update URL
    const newPagination = { current: 1, pageSize: pagination.pageSize };
    updateURL(processedFilters, newPagination, { field: sortField, order: sortOrder });

    loadRules(1, pagination.pageSize, processedFilters, getSortParams());
    setFilterDrawerVisible(false);
  };

  // Clear all filters
  const handleFilterClear = () => {
    form.resetFields();
    setFilters({});

    // Clear URL params (keep sort if any)
    const newPagination = { current: 1, pageSize: pagination.pageSize };
    updateURL({}, newPagination, { field: sortField, order: sortOrder });

    loadRules(1, pagination.pageSize, {}, getSortParams());
    setFilterDrawerVisible(false);
  };

  // Format currency
  const formatCurrency = (amount, currency) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount);
  };

  // Format date with timezone
  const formatDateWithTimezone = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  // Get status color
  const getStatusColor = (status) => {
    const colors = {
      DRAFT: 'default',
      PENDING: 'processing',
      APPROVED: 'success',
      REJECTED: 'error',
      PENDING_DELETION: 'warning'
    };
    return colors[status] || 'default';
  };

  // Render fee structure
  const renderFeeStructure = (record) => {
    const fees = [];

    if (record.flatFee) {
      fees.push(`${formatCurrency(record.flatFee, record.feeCurrencyCode)}`);
    }

    if (record.percentageRate) {
      fees.push(`${record.percentageRate}%`);
    }

    if (record.minimumFee) {
      fees.push(`Min: ${formatCurrency(record.minimumFee, record.feeCurrencyCode)}`);
    }

    if (record.maximumFee) {
      fees.push(`Max: ${formatCurrency(record.maximumFee, record.feeCurrencyCode)}`);
    }

    if (fees.length > 0) return fees.join(' | ');

    // Fallback: show tiered pricing summary if no flat/percentage fees
    if (record.tieredPricing) {
      const tp = record.tieredPricing;
      let tierCount = 0;
      if (tp.tier1End || tp.tier1FixedFee || tp.tier1MDR) tierCount++;
      if (tp.tier2End || tp.tier2FixedFee || tp.tier2MDR) tierCount++;
      if (tp.tier3End || tp.tier3FixedFee || tp.tier3MDR) tierCount++;

      const parts = [`Tiered (${tierCount} tier${tierCount > 1 ? 's' : ''})`];
      if (tp.tierCountModel) parts.push(tp.tierCountModel);
      return parts.join(' | ');
    }

    return 'No fee structure';
  };

  // Render costing details based on available data
  const renderCostingDetails = (record) => {
    const details = [];

    // Payout-related details
    if (record.payoutRailName) {
      details.push(`Rail: ${record.payoutRailName}`);
    }
    if (record.payoutConnectorName) {
      details.push(`Connector: ${record.payoutConnectorName}`);
    }

    // Card-related details
    if (record.acquirerName) {
      details.push(`Acquirer: ${record.acquirerName}`);
    }
    if (record.cardBrandName) {
      details.push(`Brand: ${record.cardBrandName}`);
    }
    if (record.cardType && record.cardType !== 'N/A') {
      details.push(`Type: ${record.cardType}`);
    }
    if (record.cardTier) {
      details.push(`Tier: ${record.cardTier}`);
    }

    // Other details
    if (record.industryName) {
      details.push(`Industry: ${record.industryName}`);
    }
    if (record.flow) {
      details.push(`Flow: ${record.flow}`);
    }
    if (record.processor) {
      details.push(`Processor: ${record.processor}`);
    }
    if (record.businessId) {
      details.push(`Business: ${record.businessId}`);
    }

    // FX-specific details
    if (record.liquidityPartner) {
      details.push(`Partner: ${record.liquidityPartner}`);
    }
    if (record.sourceCurrencyCode && record.destinationCurrencyCode) {
      details.push(`Pair: ${record.sourceCurrencyCode}/${record.destinationCurrencyCode}`);
    }

    return details.length > 0 ? (
      <div>
        {details.map((detail, index) => (
          <div key={index} style={{ fontSize: '11px' }}>
            <Text type="secondary">{detail}</Text>
          </div>
        ))}
      </div>
    ) : 'N/A';
  };

  // Render tiered pricing details for expanded row
  const renderTieredPricing = (tieredPricing, currency) => {
    if (!tieredPricing) return null;

    const tiers = [];

    if (tieredPricing.tier1End || tieredPricing.tier1FixedFee || tieredPricing.tier1MDR) {
      const tier1Details = [];
      if (tieredPricing.tier1End) tier1Details.push(`Up to ${tieredPricing.tier1End}`);
      if (tieredPricing.tier1FixedFee) tier1Details.push(`Fixed: ${formatCurrency(tieredPricing.tier1FixedFee, currency)}`);
      if (tieredPricing.tier1MDR) tier1Details.push(`MDR: ${tieredPricing.tier1MDR}%`);
      tiers.push(`Tier 1: ${tier1Details.join(', ')}`);
    }

    if (tieredPricing.tier2End || tieredPricing.tier2FixedFee || tieredPricing.tier2MDR) {
      const tier2Details = [];
      if (tieredPricing.tier2End) tier2Details.push(`Up to ${tieredPricing.tier2End}`);
      if (tieredPricing.tier2FixedFee) tier2Details.push(`Fixed: ${formatCurrency(tieredPricing.tier2FixedFee, currency)}`);
      if (tieredPricing.tier2MDR) tier2Details.push(`MDR: ${tieredPricing.tier2MDR}%`);
      tiers.push(`Tier 2: ${tier2Details.join(', ')}`);
    }

    if (tieredPricing.tier3End || tieredPricing.tier3FixedFee || tieredPricing.tier3MDR) {
      const tier3Details = [];
      if (tieredPricing.tier3End) tier3Details.push(`Up to ${tieredPricing.tier3End}`);
      if (tieredPricing.tier3FixedFee) tier3Details.push(`Fixed: ${formatCurrency(tieredPricing.tier3FixedFee, currency)}`);
      if (tieredPricing.tier3MDR) tier3Details.push(`MDR: ${tieredPricing.tier3MDR}%`);
      tiers.push(`Tier 3: ${tier3Details.join(', ')}`);
    }

    return tiers.length > 0 ? tiers : null;
  };

  // Handle rule details view
  const handleViewDetails = (ruleId) => {
    setSelectedRuleId(ruleId);
    setDetailsVisible(true);
  };

  // Handle rule edit
  const handleEditRule = (rule, editMode = null) => {
    const ruleObj = typeof rule === 'object' ? rule : rules.find(r => r.id === rule);
    const ruleId = ruleObj?.id || rule;

    // Automatically determine edit mode based on rule status
    let finalEditMode = editMode;
    if (!finalEditMode) {
      // For approved rules, always use edit-as-new
      if (ruleObj?.status === 'APPROVED') {
        finalEditMode = 'edit-as-new';
      } else {
        finalEditMode = 'edit';
      }
    }

    // Preserve current URL params so we can return to the same state
    const currentParams = searchParams.toString();
    const returnPath = currentParams ? `?${currentParams}` : '';

    if (finalEditMode === 'edit-as-new') {
      navigate(`/costing-rules/${ruleId}/edit-as-new`, { state: { returnPath } });
    } else {
      navigate(`/costing-rules/${ruleId}/edit`, { state: { returnPath } });
    }
  };

  // Handle rule delete
  const handleDeleteRule = (ruleId) => {
    Modal.confirm({
      title: 'Delete COGS Rule',
      content: 'Are you sure you want to delete this COGS rule? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await cogsService.deleteRule(ruleId);
          message.success('COGS rule deleted successfully');
          loadRules(pagination.current, pagination.pageSize, filters, getSortParams());
        } catch (error) {
          message.error('Failed to delete COGS rule');
        }
      }
    });
  };

  // Handle create rule success
  const handleCreateRuleSuccess = async () => {
    // Refresh data after successful creation
    loadRules(pagination.current, pagination.pageSize, filters, getSortParams());
  };

  // Handle create rule cancel
  const handleCreateRuleCancel = () => {
    setCreateModalVisible(false);
  };

  // Handle Attach URL
  const handleAttachUrl = (record) => {
    setAttachUrlRule(record);
    setAttachUrlVisible(true);
  };

  const handleAttachUrlSave = async (values) => {
    setAttachUrlLoading(true);
    try {
      await cogsService.updateDescription(attachUrlRule.id, values.description);
      message.success('Description updated successfully');
      setAttachUrlVisible(false);
      setAttachUrlRule(null);
      loadRules(pagination.current, pagination.pageSize, filters, getSortParams());
    } catch (error) {
      message.error('Failed to update description');
    } finally {
      setAttachUrlLoading(false);
    }
  };

  // Handle column resize
  const handleColumnResize = useCallback((key) => (_e, { size }) => {
    setColumnWidths((prev) => {
      const updated = { ...prev, [key]: size.width };
      saveColumnWidths(updated);
      return updated;
    });
  }, []);

  // Render expanded row details
  const expandedRowRender = (record) => {
    const tieredPricingTiers = renderTieredPricing(record.tieredPricing, record.feeCurrencyCode);

    return (
      <div style={{ padding: '12px 16px', background: '#fafafa' }}>
        {/* Fee Details */}
        <Descriptions
          title="Fee Details"
          bordered
          column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="Fee Currency">
            {record.feeCurrencyCode || 'N/A'}
          </Descriptions.Item>
          {record.flatFee !== null && record.flatFee !== undefined && (
            <Descriptions.Item label="Flat Fee">
              {formatCurrency(record.flatFee, record.feeCurrencyCode)}
            </Descriptions.Item>
          )}
          {record.percentageRate !== null && record.percentageRate !== undefined && (
            <Descriptions.Item label="Percentage Rate">
              {record.percentageRate}%
            </Descriptions.Item>
          )}
          {record.minimumFee !== null && record.minimumFee !== undefined && (
            <Descriptions.Item label="Minimum Fee">
              {formatCurrency(record.minimumFee, record.feeCurrencyCode)}
            </Descriptions.Item>
          )}
          {record.maximumFee !== null && record.maximumFee !== undefined && (
            <Descriptions.Item label="Maximum Fee">
              {formatCurrency(record.maximumFee, record.feeCurrencyCode)}
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* Tiered Pricing (conditional) */}
        {record.tieredPricing && (
          <Descriptions
            title="Tiered Pricing"
            bordered
            column={2}
            size="small"
            style={{ marginBottom: 16 }}
          >
            {record.tieredPricing.tierAllOrStack && (
              <Descriptions.Item label="Type">
                <Tag color="blue">{record.tieredPricing.tierAllOrStack}</Tag>
              </Descriptions.Item>
            )}
            {record.tieredPricing.tierCountModel && (
              <Descriptions.Item label="Model">
                <Tag color="green">{record.tieredPricing.tierCountModel}</Tag>
              </Descriptions.Item>
            )}
            {tieredPricingTiers && (
              <Descriptions.Item label="Tiers" span={2}>
                {tieredPricingTiers.map((tier, idx) => (
                  <div key={idx} style={{ marginBottom: 4 }}>
                    <Text code style={{ fontSize: '12px' }}>{tier}</Text>
                  </div>
                ))}
              </Descriptions.Item>
            )}
          </Descriptions>
        )}

        {/* Costing Details */}
        <Descriptions
          title="Costing Details"
          bordered
          column={{ xxl: 4, xl: 3, lg: 3, md: 2, sm: 1, xs: 1 }}
          size="small"
          style={{ marginBottom: 16 }}
        >
          {record.payoutRailName && (
            <Descriptions.Item label="Payout Rail">{record.payoutRailName}</Descriptions.Item>
          )}
          {record.payoutConnectorName && (
            <Descriptions.Item label="Payout Connector">{record.payoutConnectorName}</Descriptions.Item>
          )}
          {record.acquirerName && (
            <Descriptions.Item label="Acquirer">{record.acquirerName}</Descriptions.Item>
          )}
          {record.cardBrandName && (
            <Descriptions.Item label="Card Brand">{record.cardBrandName}</Descriptions.Item>
          )}
          {record.cardType && record.cardType !== 'N/A' && (
            <Descriptions.Item label="Card Type"><Tag>{record.cardType}</Tag></Descriptions.Item>
          )}
          {record.cardTier && (
            <Descriptions.Item label="Card Tier">{record.cardTier}</Descriptions.Item>
          )}
          {record.cardLocalForeign && record.cardLocalForeign !== 'N/A' && (
            <Descriptions.Item label="Card Local/Foreign"><Tag>{record.cardLocalForeign}</Tag></Descriptions.Item>
          )}
          {record.foreignLocalMerchant && record.foreignLocalMerchant !== 'N/A' && (
            <Descriptions.Item label="Merchant Type"><Tag>{record.foreignLocalMerchant}</Tag></Descriptions.Item>
          )}
          {record.industryName && (
            <Descriptions.Item label="Industry">{record.industryName}</Descriptions.Item>
          )}
          {record.flow && (
            <Descriptions.Item label="Flow">{record.flow}</Descriptions.Item>
          )}
          {record.processor && (
            <Descriptions.Item label="Processor">{record.processor}</Descriptions.Item>
          )}
          {record.businessId && (
            <Descriptions.Item label="Business ID">{record.businessId}</Descriptions.Item>
          )}
          {record.fundSource && (
            <Descriptions.Item label="Fund Source">{record.fundSource}</Descriptions.Item>
          )}
          {record.onUs && (
            <Descriptions.Item label="On-Us">{record.onUs}</Descriptions.Item>
          )}
          {((record.mccs && record.mccs.length > 0) || record.mcc) && (
            <Descriptions.Item label="MCCs">
              {record.mccs && record.mccs.length > 0
                ? record.mccs.map((mccObj, index) => <Tag key={index}>{mccObj.mcc}</Tag>)
                : <Text code>{record.mcc}</Text>}
            </Descriptions.Item>
          )}
          {record.sourceCurrencyCode && (
            <Descriptions.Item label="Source Currency">{record.sourceCurrencyCode}</Descriptions.Item>
          )}
          {record.destinationCurrencyCode && (
            <Descriptions.Item label="Destination Currency">{record.destinationCurrencyCode}</Descriptions.Item>
          )}
          {record.liquidityPartner && (
            <Descriptions.Item label="Liquidity Partner">{record.liquidityPartner}</Descriptions.Item>
          )}
          {record.baseRateSource && (
            <Descriptions.Item label="Base Rate Source">{record.baseRateSource}</Descriptions.Item>
          )}
          {record.bidSpreadBps != null && (
            <Descriptions.Item label="Bid Spread (bps)">{record.bidSpreadBps}</Descriptions.Item>
          )}
          {record.askSpreadBps != null && (
            <Descriptions.Item label="Ask Spread (bps)">{record.askSpreadBps}</Descriptions.Item>
          )}
          {record.offmarketSpreadBps != null && (
            <Descriptions.Item label="Off-Market Spread (bps)">{record.offmarketSpreadBps}</Descriptions.Item>
          )}
          {record.weekendHolidaySpreadBps != null && (
            <Descriptions.Item label="Weekend/Holiday Spread (bps)">{record.weekendHolidaySpreadBps}</Descriptions.Item>
          )}
          {record.valueDateConvention && (
            <Descriptions.Item label="Value Date Convention">{record.valueDateConvention}</Descriptions.Item>
          )}
          {record.installmentTerm && (
            <Descriptions.Item label="Installment Term">{record.installmentTerm}</Descriptions.Item>
          )}
          {record.installmentInterest !== null && record.installmentInterest !== undefined && (
            <Descriptions.Item label="Installment Interest">
              <Tag color={record.installmentInterest ? 'green' : 'default'}>
                {record.installmentInterest ? 'Yes' : 'No'}
              </Tag>
            </Descriptions.Item>
          )}
        </Descriptions>

        {/* MIDs */}
        {((record.mids && record.mids.length > 0) || record.mid) && (
          <Descriptions
            title="MIDs"
            bordered
            column={1}
            size="small"
            style={{ marginBottom: 16 }}
          >
            <Descriptions.Item label="Merchant IDs">
              {record.mids && record.mids.length > 0 ? (
                <div>
                  {record.mids.map((midObj, index) => (
                    <div key={index} style={{ marginBottom: 4 }}>
                      <Text code style={{ fontSize: '12px' }}>
                        {midObj.mid}
                        {midObj.subMid && <Text type="secondary"> (Sub: {midObj.subMid})</Text>}
                      </Text>
                    </div>
                  ))}
                </div>
              ) : (
                <Text code style={{ fontSize: '12px' }}>
                  {record.mid}
                  {record.subMid && <Text type="secondary"> (Sub: {record.subMid})</Text>}
                </Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* Metadata */}
        <Descriptions
          title="Metadata"
          bordered
          column={{ xxl: 3, xl: 3, lg: 2, md: 2, sm: 1, xs: 1 }}
          size="small"
        >
          <Descriptions.Item label="Proposer">
            {record.proposer?.email || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Effective Start">
            {formatDateWithTimezone(record.effectiveStartDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Effective End">
            {formatDateWithTimezone(record.effectiveEndDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Created">
            {formatDateWithTimezone(record.createdAt)}
          </Descriptions.Item>
          <Descriptions.Item label="Updated">
            {formatDateWithTimezone(record.updatedAt)}
          </Descriptions.Item>
          {record.description && (
            <Descriptions.Item label="Description" span={3}>
              {record.description}
            </Descriptions.Item>
          )}
        </Descriptions>
      </div>
    );
  };

  // Table columns configuration with resizable widths
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => {
  const baseColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      sorter: true,
    },
    {
      title: 'Entity',
      dataIndex: ['entity', 'name'],
      key: 'entity',
      width: 130,
      ellipsis: true,
      render: (text, record) => (
        <div>
          <Text strong>{text || 'N/A'}</Text>
          {record.entity?.country && (
            <div><Tag size="small">{record.entity.country}</Tag></div>
          )}
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: ['product', 'name'],
      key: 'product',
      width: 120,
      ellipsis: true,
    },
    {
      title: 'Channel',
      dataIndex: ['channel'],
      key: 'channel',
      width: 120,
      ellipsis: true,
      render: (channel) => {
        if (!channel) return 'N/A';
        return (
          <div>
            <Text strong>{channel.name || 'N/A'}</Text>
            {channel.code && (
              <div><Text code style={{ fontSize: '11px' }}>{channel.code}</Text></div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Pricing Scheme',
      dataIndex: ['scheme'],
      key: 'scheme',
      width: 180,
      ellipsis: true,
      render: (scheme) => {
        if (!scheme) return 'N/A';
        const text = scheme.code && scheme.description
          ? `${scheme.code} - ${scheme.description}`
          : scheme.code || scheme.description || 'N/A';
        return (
          <Tooltip title={text} placement="topLeft">
            <span>{text}</span>
          </Tooltip>
        );
      },
    },
    {
      title: 'Fee Structure',
      key: 'feeStructure',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const feeText = renderFeeStructure(record);
        return (
          <Tooltip title={feeText} placement="topLeft">
            <Text code style={{ fontSize: '12px' }}>
              {feeText}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Currency',
      dataIndex: 'feeCurrencyCode',
      key: 'currency',
      width: 80,
    },
    {
      title: 'Costing Details',
      key: 'costingDetails',
      width: 160,
      ellipsis: true,
      render: (_, record) => {
        const content = renderCostingDetails(record);
        if (content === 'N/A') return content;
        return (
          <Tooltip title={content} placement="topLeft">
            <div>{content}</div>
          </Tooltip>
        );
      },
    },
    {
      title: 'MIDs',
      key: 'mids',
      width: 130,
      ellipsis: true,
      render: (_, record) => {
        if (!record.mids || record.mids.length === 0) {
          if (record.mid) {
            const midText = record.mid + (record.subMid ? ` (${record.subMid})` : '');
            return (
              <Tooltip title={midText} placement="topLeft">
                <Text code style={{ fontSize: '11px' }}>{midText}</Text>
              </Tooltip>
            );
          }
          return 'N/A';
        }

        const midsText = record.mids.map(midObj =>
          midObj.subMid ? `${midObj.mid} (${midObj.subMid})` : midObj.mid
        ).join(', ');

        return (
          <Tooltip title={midsText} placement="topLeft">
            <Text code style={{ fontSize: '11px' }}>
              {midsText}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status, record) => {
        // If rule is inactive, show INACTIVE instead of actual status
        if (record.isActive === false) {
          return <Tag color="default">INACTIVE</Tag>;
        }
        return <Tag color={getStatusColor(status)}>{status}</Tag>;
      },
    },
    {
      title: 'Effective Date',
      dataIndex: 'effectiveStartDate',
      key: 'effectiveDate',
      width: 140,
      render: (date) => formatDateWithTimezone(date),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record.id)}
            />
          </Tooltip>
          {/* Attach URL - available on active DRAFT, APPROVED, and PENDING rules for users with access */}
          {record.isActive !== false && ['DRAFT', 'APPROVED', 'PENDING'].includes(record.status) && (userData?.role === 'Admin' || record.proposerId === userData?.id) && (
            <Tooltip title="Attach URL / Update Description">
              <Button
                type="text"
                icon={<LinkOutlined />}
                onClick={() => handleAttachUrl(record)}
              />
            </Tooltip>
          )}
          {/* Don't show edit/delete buttons for INACTIVE rules */}
          {record.isActive !== false && (userData?.role === 'Admin' || record.proposerId === userData?.id) && (
            <>
              <Tooltip title={record.status === 'APPROVED' ? 'Edit as New Version' : 'Edit'}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEditRule(record)}
                />
              </Tooltip>
              <Tooltip title="Delete">
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteRule(record.id)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];

  return baseColumns.map((col) => {
    const savedWidth = columnWidths[col.key];
    const width = savedWidth || col.width;

    // Don't make fixed columns resizable
    if (col.fixed || !width) {
      return col;
    }

    return {
      ...col,
      width,
      onHeaderCell: (column) => ({
        width: column.width,
        onResize: handleColumnResize(col.key),
      }),
    };
  });
  }, [columnWidths, handleColumnResize]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>All COGS Rules</Title>
          <Text type="secondary">
            Comprehensive view of all Cost of Goods Sold rules with advanced filtering
          </Text>
        </div>

        {/* Action Bar */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col flex="auto">
            <Space>
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFilterDrawerVisible(true)}
              >
                Advanced Filters
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => loadRules(pagination.current, pagination.pageSize, filters, getSortParams())}
              >
                Refresh
              </Button>
            </Space>
          </Col>
          <Col>
            <Button
              type="primary"
              onClick={() => setCreateModalVisible(true)}
            >
              Create New Rule
            </Button>
          </Col>
        </Row>

        {/* Active Filters Display */}
        {Object.keys(filters).length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <Text strong>Active Filters: </Text>
            {Object.entries(filters).map(([key, value]) => (
              <Tag
                key={key}
                closable
                onClose={() => {
                  const newFilters = { ...filters };
                  delete newFilters[key];
                  setFilters(newFilters);

                  // Also remove from form
                  form.setFieldValue(key, undefined);

                  // Update URL
                  const newPagination = { current: 1, pageSize: pagination.pageSize };
                  updateURL(newFilters, newPagination, { field: sortField, order: sortOrder });

                  loadRules(1, pagination.pageSize, newFilters, getSortParams());
                }}
              >
                {key}: {Array.isArray(value) ? value.join(', ') : value}
              </Tag>
            ))}
          </div>
        )}

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={rules}
          rowKey="id"
          loading={loading}
          components={{
            header: { cell: ResizableTitle },
          }}
          expandable={{
            expandedRowRender,
            rowExpandable: () => true,
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} rules`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
          size="small"
        />
      </Card>

      {/* Advanced Filters Drawer */}
      <Drawer
        title="Advanced Filters"
        placement="right"
        width={400}
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        extra={
          <Space>
            <Button onClick={handleFilterClear}>Clear</Button>
            <Button type="primary" onClick={handleFilterApply}>
              Apply
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item name="search" label="Search">
            <Input
              placeholder="Search by description, ID, etc."
              prefix={<SearchOutlined />}
            />
          </Form.Item>

          <Form.Item name="status" label="Status">
            <Select
              mode="multiple"
              placeholder="Select status"
              allowClear
            >
              <Option value="DRAFT">Draft</Option>
              <Option value="PENDING">Pending</Option>
              <Option value="APPROVED">Approved</Option>
              <Option value="REJECTED">Rejected</Option>
            </Select>
          </Form.Item>

          <Form.Item name="entityId" label="Entity">
            <Select
              mode="multiple"
              placeholder="Select entities"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                const optionText = typeof option.children === 'string'
                  ? option.children.toLowerCase()
                  : option.label?.toLowerCase() || '';
                return optionText.indexOf(searchText) >= 0;
              }}
            >
              {dimensions.entities?.map(entity => (
                <Option key={entity.id} value={entity.id} label={entity.name}>
                  {entity.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="productId" label="Product">
            <Select
              mode="multiple"
              placeholder="Select products"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                const optionText = typeof option.children === 'string'
                  ? option.children.toLowerCase()
                  : option.label?.toLowerCase() || '';
                return optionText.indexOf(searchText) >= 0;
              }}
            >
              {dimensions.products?.map(product => (
                <Option key={product.id} value={product.id} label={product.name}>
                  {product.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="channelId" label="Channel">
            <Select
              mode="multiple"
              placeholder="Select channels"
              allowClear
              showSearch
              filterOption={(input, option) => {
                const searchText = input.toLowerCase();
                const optionText = typeof option.children === 'string'
                  ? option.children.toLowerCase()
                  : option.label?.toLowerCase() || '';
                return optionText.indexOf(searchText) >= 0;
              }}
            >
              {dimensions.channels?.map(channel => (
                <Option key={channel.id} value={channel.id} label={`${channel.name} ${channel.code ? `(${channel.code})` : ''}`}>
                  {channel.name} {channel.code && `(${channel.code})`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="schemeId" label="Pricing Scheme">
            <Select
              mode="multiple"
              placeholder="Select schemes"
              allowClear
            >
              {dimensions.schemes?.map(scheme => (
                <Option key={scheme.id} value={scheme.id}>
                  {scheme.code && scheme.description
                    ? `${scheme.code} - ${scheme.description}`
                    : scheme.code || scheme.description || scheme.scheme || 'N/A'}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="feeCurrencyCode" label="Currency">
            <Select
              mode="multiple"
              placeholder="Select currencies"
              allowClear
            >
              {dimensions.currencies?.map(currency => (
                <Option key={currency.code} value={currency.code}>
                  {currency.code} - {currency.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="mid" label="MID (Merchant ID)">
            <Input
              placeholder="Search by MID"
              allowClear
            />
          </Form.Item>

          <Form.Item name="proposerId" label="Proposer">
            <Select
              placeholder="Select proposer"
              allowClear
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {dimensions.users?.map(user => (
                <Option key={user.id} value={user.id}>
                  {user.email}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item name="dateRange" label="Date Range">
            <RangePicker
              style={{ width: '100%' }}
              showTime
            />
          </Form.Item>


        </Form>
      </Drawer>

      {/* Rule Details Modal */}
      <CostingRuleDetails
        ruleId={selectedRuleId}
        visible={detailsVisible}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
        }}
        onEdit={handleEditRule}
        onDelete={(rule) => {
          setDetailsVisible(false);
          setSelectedRuleId(null);
          handleDeleteRule(rule.id);
        }}
        refreshData={() => loadRules(pagination.current, pagination.pageSize, filters, getSortParams())}
      />

      {/* Create Rule Modal */}
      <COGSCreationModal
        visible={createModalVisible}
        onCancel={handleCreateRuleCancel}
        onSuccess={handleCreateRuleSuccess}
        title="Create New COGS Rule"
        width={1200}
      />

      {/* Attach URL / Update Description Modal */}
      <Modal
        title="Attach URL / Update Description"
        open={attachUrlVisible}
        onCancel={() => { setAttachUrlVisible(false); setAttachUrlRule(null); }}
        footer={null}
        destroyOnClose
      >
        {attachUrlRule && (
          <Form
            layout="vertical"
            initialValues={{ description: attachUrlRule.description || '' }}
            onFinish={handleAttachUrlSave}
          >
            <Form.Item label="Rule">
              <Text type="secondary">
                ID: {attachUrlRule.id} — {attachUrlRule.entity?.name || 'N/A'} / {attachUrlRule.product?.name || 'N/A'}
              </Text>
            </Form.Item>
            <Form.Item
              name="description"
              label="Description / URL"
              rules={[
                { required: true, message: 'Please enter a description or URL' },
                { max: 1000, message: 'Must not exceed 1000 characters' },
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder="Paste a URL (e.g. Google Drive link) or enter a description..."
                showCount
                maxLength={1000}
              />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
              <Space>
                <Button onClick={() => { setAttachUrlVisible(false); setAttachUrlRule(null); }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={attachUrlLoading}>
                  Save
                </Button>
              </Space>
            </Form.Item>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default AllCOGSRulesPage;
